import React, { useEffect, useState } from "react";
import Brand from "@/components/Brand";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkle, Play, Pause, CheckCircle, Waveform, Lock, Eye, ShieldCheck,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GlassCard } from "@/components/glass/GlassCard";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1765164478579-2c041dad2c7d?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";

function money(v, ccy = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(v || 0);
}

function StanceRow({ clause }) {
  const s = clause.stance || "neutral";
  const color = s === "for" ? "emerald" : s === "against" ? "rose" : "slate";
  return (
    <div className={`p-4 rounded-lg border bg-[var(--surface)] border-${color}-500/20`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`chip chip-${s === "for" ? "low" : s === "against" ? "high" : "neutral"}`}>{s}</span>
        <span className="text-xs text-[var(--text-3)]">{clause.category}</span>
      </div>
      <div className="text-sm font-medium mb-1">{clause.title}</div>
      <div className="text-xs text-[var(--text-2)] leading-relaxed">{clause.plain_language}</div>
    </div>
  );
}

export default function SignatoryExperience() {
  const { id } = useParams();
  const nav = useNavigate();
  const [contract, setContract] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [signed, setSigned] = useState(false);
  const audioRef = React.useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/contracts/${id}`);
        setContract(data);
        try {
          const a = await api.get(`/analyze/${id}`);
          setAnalysis(a.data);
        } catch {
          // Auto-analyze
          toast.info("Running AI analysis…");
          const res = await api.post("/analyze", { contract_id: id, perspective: "signatory" });
          setAnalysis(res.data);
        }
      } catch (e) {
        toast.error("Contract not found");
      }
    })();
  }, [id]);

  const fetchAudio = async () => {
    setAudioLoading(true);
    try {
      const token = localStorage.getItem("clariva_token");
      const res = await fetch(`${API_BASE}/audio-briefing/${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      setAudioUrl(URL.createObjectURL(blob));
      setTimeout(() => audioRef.current?.play(), 100);
    } catch {
      toast.error("Audio failed");
    } finally { setAudioLoading(false); }
  };

  const onOtpChange = (i, v) => {
    const val = v.slice(-1).replace(/\D/g, "");
    const newOtp = [...otp];
    newOtp[i] = val;
    setOtp(newOtp);
    if (val && i < 5) document.getElementById(`otp-${i+1}`)?.focus();
  };

  const canSign = otp.every((d) => d.length === 1);

  const sign = async () => {
    if (!canSign) return;
    await api.patch(`/contracts/${id}`, { status: "active" });
    setSigned(true);
    toast.success("Contract signed");
  };

  if (!contract) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[var(--text-2)] text-sm animate-pulse">Loading contract…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="fixed inset-0 -z-10">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg)]/30 via-[var(--bg)]/60 to-[var(--bg)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-8 pb-16">
        {/* Header */}
        <motion.div
          initial={{ y: 40, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="flex items-center gap-3 mb-8">
            <Brand size={36} />
            <div>
              <div className="overline text-[var(--accent)]">Clariva — Transparent Signatory Experience</div>
              <div className="text-sm text-[var(--text-2)]">You're being asked to sign. Here's everything you need to know.</div>
            </div>
          </div>
        </motion.div>

        {signed ? (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <GlassCard strong className="p-12 text-center max-w-2xl mx-auto">
              <CheckCircle size={64} weight="fill" className="text-emerald-400 mx-auto mb-4" />
              <h1 className="text-3xl font-medium mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Signed with confidence</h1>
              <p className="text-[var(--text-2)] mb-6">A signed copy has been sent to both parties.</p>
              <Button onClick={() => nav(`/app/repository/${id}`)} data-testid="return-to-app-btn" className="gap-2">
                Back to dashboard
              </Button>
            </GlassCard>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* LEFT: AI Breakdown */}
            <motion.div
              initial={{ x: -40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              <GlassCard strong className="p-6 md:p-8 h-full">
                <div className="overline mb-2 flex items-center gap-2"><Sparkle size={12} weight="fill" className="text-[var(--accent)]" /> AI transparency briefing</div>
                <h1 className="text-3xl md:text-4xl font-medium tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{contract.title}</h1>
                <div className="text-sm text-[var(--text-2)] mb-6">From <strong>{contract.counterparty}</strong> · {money(contract.value_amount, contract.value_currency)}</div>

                {analysis && (
                  <>
                    <div className="mb-6 text-lg leading-relaxed" style={{ fontFamily: 'var(--font-heading)', fontWeight: 400 }}>
                      {analysis.plain_language_summary}
                    </div>

                    {/* Audio briefing */}
                    <div className="mb-6 p-4 rounded-lg bg-[var(--surface)] border border-[var(--border)] flex items-center gap-4">
                      <div className="flex items-center gap-1 h-10 w-20 justify-center">
                        {[0,1,2,3,4,5].map((i) => (
                          <span key={i} className="wave-bar" style={{ animationDelay: `${i*0.08}s`, opacity: playing ? 1 : 0.4 }} />
                        ))}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium">Audio briefing</div>
                        <div className="text-xs text-[var(--text-3)]">60s summary, voiced by AI</div>
                      </div>
                      {audioUrl ? (
                        <>
                          <audio ref={audioRef} src={audioUrl} onEnded={() => setPlaying(false)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
                          <Button size="sm" onClick={() => { playing ? audioRef.current.pause() : audioRef.current.play(); }} data-testid="signatory-audio-btn" className="gap-2">
                            {playing ? <Pause size={12} weight="fill" /> : <Play size={12} weight="fill" />}
                            {playing ? "Pause" : "Play"}
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" onClick={fetchAudio} disabled={audioLoading} data-testid="signatory-audio-generate-btn" className="gap-2">
                          <Waveform size={12} />
                          {audioLoading ? "…" : "Listen"}
                        </Button>
                      )}
                    </div>

                    {/* For / Against / Neutral summary */}
                    <div className="grid grid-cols-3 gap-2 mb-6">
                      {["for", "neutral", "against"].map((s) => {
                        const count = (analysis.clauses || []).filter((c) => c.stance === s).length;
                        return (
                          <div key={s} className="text-center p-3 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                            <div className="text-2xl font-mono font-semibold">{count}</div>
                            <div className={`text-xs uppercase tracking-wider mt-1 ${s === "for" ? "text-emerald-400" : s === "against" ? "text-rose-400" : "text-[var(--text-3)]"}`}>{s} you</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Clauses list */}
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-2">
                      {(analysis.clauses || []).map((c, i) => (
                        <StanceRow key={c.id || `clause-${c.title || ""}-${i}`} clause={c} />
                      ))}
                    </div>
                  </>
                )}

                {!analysis && (
                  <div className="text-sm text-[var(--text-2)] py-12 text-center animate-pulse">
                    AI is analyzing this contract from your perspective…
                  </div>
                )}
              </GlassCard>
            </motion.div>

            {/* RIGHT: Contract + OTP */}
            <motion.div
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="space-y-6"
            >
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="overline">Contract text</div>
                  <span className="chip chip-neutral"><Eye size={10} weight="fill" className="inline mr-1" /> Read-only</span>
                </div>
                <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-[var(--text-2)] max-h-[45vh] overflow-y-auto">
                  {contract.content}
                </pre>
              </GlassCard>

              <GlassCard strong className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-md bg-[var(--surface-strong)] border border-[var(--border-strong)] flex items-center justify-center">
                    <Lock size={16} weight="duotone" className="text-[var(--accent)]" />
                  </div>
                  <div>
                    <div className="overline">Sign with OTP</div>
                    <div className="text-xs text-[var(--text-3)]">Enter the 6-digit code sent to your email</div>
                  </div>
                </div>
                <div className="flex gap-2 mb-4">
                  {otp.map((d, i) => (
                    <Input
                      key={i}
                      id={`otp-${i}`}
                      value={d}
                      onChange={(e) => onOtpChange(i, e.target.value)}
                      maxLength={1}
                      data-testid={`otp-input-${i}`}
                      inputMode="numeric"
                      className="w-12 h-14 text-center text-xl font-mono glass-subtle border-[var(--border-strong)]"
                    />
                  ))}
                </div>
                <div className="text-xs text-[var(--text-3)] mb-4">Demo: enter any 6 digits.</div>
                <Button onClick={sign} disabled={!canSign} data-testid="sign-contract-btn" className="w-full h-12 gap-2">
                  <ShieldCheck size={16} weight="fill" /> Sign contract
                </Button>
                <div className="mt-4 text-xs text-[var(--text-3)] text-center">
                  Powered by Clariva · Audit trail encrypted · eIDAS-ready
                </div>
              </GlassCard>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
